import BillingModel from "../../../types/billing-model";
import BillingFormData, {
  BillingFormDataContributions,
  User,
} from "../types/billing-form.data";
import BillingsNode from "../../../common/types/billings-node";
import googleMapsRouteService from "../../../../../common/utils/google-maps-route/google-maps-route.service";
import BillingsTaxiDriverContributionBonus, {
  BillingsTaxiDriverContributionBonusType,
} from "../../common/contributions/bonus/types/billings-taxi-driver-contributions-bonus";
import BillingsTaxiDriverContributionPenalty, {
  BillingsTaxiDriverContributionPenaltyType,
} from "../../common/contributions/penalty/types/billings-taxi-driver-contributions-penalty";
import BillingsTaxiDriverContributionContractCorrection, {
  BillingsTaxiDriverContributionContractCorrectionType,
} from "../../common/contributions/contract-correction/types/billings-taxi-driver-contributions-contract-correction";
import BillingsTaxiDriverContributionsBonusTableRow from "../../common/contributions/bonus/types/billings-taxi-driver-contributions-bonus-table-row";
import BillingsTaxiDriverContributionsPenaltyTableRow from "../../common/contributions/penalty/types/billings-taxi-driver-contributions-penalty-table-row";
import BillingsTaxiDriverContributionsContractCorrectionTableRow from "../../common/contributions/contract-correction/types/billings-taxi-driver-contributions-contract-correction-table-row";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import billingsTaxiDriverContributionsBonusHelper from "../../common/contributions/bonus/billings-taxi-driver-contributions-bonus.helper";
import billingsTaxiDriverContributionsPenaltyHelper from "../../common/contributions/penalty/billings-taxi-driver-contributions-penalty.helper";
import billingsTaxiDriverContributionsContractCorrectionHelper from "../../common/contributions/contract-correction/billings-taxi-driver-contributions-contract-correction.helper";
import TabsData from "../../../../../common/components/tabs/common/types/tabs-data";
import BillingsTaxiDriverContributionsBonusTableComponent from "../../common/contributions/bonus/table/billings-taxi-driver-contributions-bonus-table.component";
import BillingsTaxiDriverContributionsPenaltyTableComponent from "../../common/contributions/penalty/table/billings-taxi-driver-contributions-penalty-table.component";
import BillingsTaxiDriverContributionsContractCorrectionTableComponent from "../../common/contributions/contract-correction/table/billings-taxi-driver-contributions-contract-correction-table.component";
import BillingsTaxiDriverDetails, {
  BillingsTaxiDriverDetailsBillingNode,
  BillingsTaxiDriverDetailsContribution,
  BillingsTaxiDriverDetailsContributionBonus,
  BillingsTaxiDriverDetailsContributionContractCorrection,
  BillingsTaxiDriverDetailsContributionPenalty,
  BillingsTaxiDriverDetailsContributionType,
  BillingsTaxiDriverDetailsDriverUser,
} from "../../../../../common/services/billings/taxi-driver/details/billings-taxi-driver-details";

const createBillingNode = (
  node: BillingsTaxiDriverDetailsBillingNode,
  index: number,
  nodes: BillingsTaxiDriverDetailsBillingNode[],
): BillingsNode => {
  const previousItem = index - 1 >= 0 ? nodes[index - 1] : null;

  const url =
    previousItem !== null
      ? googleMapsRouteService.createGoogleMapsRouteUrl([
          { latitude: previousItem.lat, longitude: previousItem.lon },
          { latitude: node.lat, longitude: node.lon },
        ])
      : "";

  const result: BillingsNode = {
    id: node.id,
    nodeId: node.nodeId,
    lat: node.lat,
    lon: node.lon,
    description: node.description,
    checkoutDate: node.checkoutDate,
    checkoutEventUuid: node.checkoutEventId,
    plannedDate: node.plannedDate,
    plannedDistance: node.plannedDistance,
    orderId: node.orderId,
    distance: node.distance,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    isHighwayAllowed: node.allowCharge,
    isEditable: node.editableCoordinates,
    googleMapsUrl: url,
    position: index + 1,
  };

  return result;
};

const createBillingNodes = (
  nodes: BillingsTaxiDriverDetailsBillingNode[],
): BillingsNode[] => {
  return nodes.map(createBillingNode);
};

const createBillingPenaltyContributionType = (
  contributionType: BillingsTaxiDriverDetailsContributionType,
): BillingsTaxiDriverContributionPenaltyType | undefined => {
  switch (contributionType) {
    case BillingsTaxiDriverDetailsContributionType.PENALTY_WRONG_ROUTE:
      return BillingsTaxiDriverContributionPenaltyType.PENALTY_WRONG_ROUTE;
    case BillingsTaxiDriverDetailsContributionType.PENALTY_BEING_LATE:
      return BillingsTaxiDriverContributionPenaltyType.PENALTY_BEING_LATE;
    case BillingsTaxiDriverDetailsContributionType.PENALTY_INCOMPATIBLE_CAR:
      return BillingsTaxiDriverContributionPenaltyType.PENALTY_INCOMPATIBLE_CAR;
    case BillingsTaxiDriverDetailsContributionType.PENALTY_OTHER:
      return BillingsTaxiDriverContributionPenaltyType.PENALTY_OTHER;
    default:
      return undefined;
  }
};

const createBillingBonusContribution = (
  contribution: BillingsTaxiDriverDetailsContributionBonus,
): BillingsTaxiDriverContributionBonus => {
  switch (contribution.type) {
    case BillingsTaxiDriverDetailsContributionType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsTaxiDriverContributionBonusType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment ?? "",
        distance: contribution.distance,
        rate: contribution.rate,
      };
    case BillingsTaxiDriverDetailsContributionType.BONUS_OTHER:
      return {
        type: BillingsTaxiDriverContributionBonusType.BONUS_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount,
        distance: null,
      };
  }
};

const createBillingContractCorrectionContribution = (
  contribution: BillingsTaxiDriverDetailsContributionContractCorrection,
): BillingsTaxiDriverContributionContractCorrection => {
  switch (contribution.type) {
    case BillingsTaxiDriverDetailsContributionType.BASE_AMOUNT_EXTERNAL_TAXI:
      return {
        type: BillingsTaxiDriverContributionContractCorrectionType.BASE_AMOUNT_EXTERNAL_TAXI,
        distance: contribution.distance,
        amount: contribution.amount,
      };
    case BillingsTaxiDriverDetailsContributionType.CONTRACT_CORRECTION_RATE:
      return {
        type: BillingsTaxiDriverContributionContractCorrectionType.CONTRACT_CORRECTION_RATE,
        rate: contribution.rate,
      };
  }
};

const createBillingPenaltyContribution = (
  contribution: BillingsTaxiDriverDetailsContributionPenalty,
): BillingsTaxiDriverContributionPenalty => {
  return {
    type: createBillingPenaltyContributionType(contribution.type) ?? null,
    comment: contribution.comment ?? "",
    amount: contribution.amount,
  };
};

const createBillingContributions = (
  contributions: BillingsTaxiDriverDetailsContribution[],
): BillingFormDataContributions => {
  const bonusContributions: BillingsTaxiDriverContributionBonus[] =
    contributions
      .filter(
        (contribution) =>
          contribution &&
          [
            BillingsTaxiDriverDetailsContributionType.BONUS_FAVORABLE_DISTANCE,
            BillingsTaxiDriverDetailsContributionType.BONUS_OTHER,
          ].includes(contribution.type),
      )
      .map((contribution) =>
        createBillingBonusContribution(
          contribution as BillingsTaxiDriverDetailsContributionBonus,
        ),
      );

  const penaltyContributions: BillingsTaxiDriverContributionPenalty[] =
    contributions
      .filter(
        (contribution) =>
          contribution &&
          [
            BillingsTaxiDriverDetailsContributionType.PENALTY_BEING_LATE,
            BillingsTaxiDriverDetailsContributionType.PENALTY_INCOMPATIBLE_CAR,
            BillingsTaxiDriverDetailsContributionType.PENALTY_OTHER,
            BillingsTaxiDriverDetailsContributionType.PENALTY_WRONG_ROUTE,
          ].includes(contribution.type),
      )
      .map((contribution) =>
        createBillingPenaltyContribution(
          contribution as BillingsTaxiDriverDetailsContributionPenalty,
        ),
      );

  const contractCorrectionContribution: BillingsTaxiDriverContributionContractCorrection[] =
    contributions
      .filter(
        (contribution) =>
          contribution &&
          [
            BillingsTaxiDriverDetailsContributionType.BASE_AMOUNT_EXTERNAL_TAXI,
            BillingsTaxiDriverDetailsContributionType.CONTRACT_CORRECTION_RATE,
          ].includes(contribution.type),
      )
      .map((contribution) =>
        createBillingContractCorrectionContribution(
          contribution as BillingsTaxiDriverDetailsContributionContractCorrection,
        ),
      );

  return {
    bonus: bonusContributions,
    penalty: penaltyContributions,
    contractCorrection: contractCorrectionContribution,
  };
};

const createDriverUser = (user: BillingsTaxiDriverDetailsDriverUser): User => {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
  };
};

const createBillingData = (data: BillingsTaxiDriverDetails) => {
  const billingDraft: BillingFormData = {
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
    contractDetails: {
      companyName: `${data.contract.taxiDriverAssociation.driver.user.firstName} ${data.contract.taxiDriverAssociation.driver.user.lastName}`,
      haltingTimeRate: data.contract.haltingTimeRate,
      distanceRate: data.contract.distanceRate,
      model: data.contract.model,
      haltingTimeAppliedAfter: data.contract.haltingTimeAfterMinutes,
      driverStartingAddress:
        data.contract.taxiDriverAssociation.driver.startingAddress.displayName,
      fleetPartner: data.contract.taxiDriverAssociation.fleetPartner?.name,
      notes: data.contract.description,
    },
    amountForChargeHaltings: data.amountForChargeHaltings,
    amountForChargeHighways: data.amountForChargeHighways,
    distance: data.distance,
    amountForDistance: data.amountForDistance,
    internalOrderId: data.planEntry.humanId,
    allContributionsAmount: data.allContributionsAmount,
    date: data.date,
    planEntryId: data.planEntry.id,
    solvedOrderUuids: data.planEntry.solvedOrders.map(
      (solvedOrder) => solvedOrder.transportingOrder.cargoOrder.id,
    ),
    solvedOrderHumanIds: data.planEntry.solvedOrders.map(
      (solvedOrder) => solvedOrder.transportingOrder.cargoOrder.humanId,
    ),
    driver: createDriverUser(data.contract.taxiDriverAssociation.driver.user),
    passengers: data.passengers,
    rateForDistance: data.rateForDistance,
    correctedContractModel: data.correctedContractModel,
    sumOfBonuses: data.sumOfBonuses,
    sumOfPenalties: data.sumOfPenalties,
  };

  return billingDraft;
};

const createContributionsBonusTableRow = (
  contribution: BillingsTaxiDriverContributionBonus,
  position: number,
): BillingsTaxiDriverContributionsBonusTableRow => {
  const translations =
    billingsTranslationsHelper.getTaxiDriverContributionsTranslations();

  const contributionType =
    billingsTaxiDriverContributionsBonusHelper.getContributionTypeTranslation(
      contribution.type,
    );

  switch (contribution.type) {
    case BillingsTaxiDriverContributionBonusType.BONUS_FAVORABLE_DISTANCE:
      return {
        id: `${contribution.type}-${position}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          distance: (
            <div title={String(contribution.distance)}>
              {String(contribution.distance)}
            </div>
          ),
          rate: (
            <div title={String(contribution.rate)}>
              {String(contribution.rate)}
            </div>
          ),
          amount: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          comment: (
            <div title={contribution.comment}>{contribution.comment}</div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    case BillingsTaxiDriverContributionBonusType.BONUS_OTHER:
      return {
        id: `${contribution.type}-${position}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          distance: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          rate: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          amount: (
            <div title={String(contribution.amount)}>
              {String(contribution.amount)}
            </div>
          ),
          comment: (
            <div title={contribution.comment}>{contribution.comment}</div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    default:
      return {
        id: String(position),
        value: {
          type: <div></div>,
          distance: <div></div>,
          rate: <div></div>,
          amount: <div></div>,
          comment: <div></div>,
          actions: <div className="d-flex"></div>,
        },
      };
  }
};

const createContributionsPenaltyTableRow = (
  contribution: BillingsTaxiDriverContributionPenalty,
  position: number,
): BillingsTaxiDriverContributionsPenaltyTableRow => {
  const contributionType =
    billingsTaxiDriverContributionsPenaltyHelper.getContributionTypeTranslation(
      contribution.type,
    );

  return {
    id: `${contribution.type}-${position}`,
    value: {
      type: <div title={contributionType}>{contributionType}</div>,
      amount: (
        <div title={String(contribution.amount)}>
          {String(contribution.amount)}
        </div>
      ),
      comment: <div title={contribution.comment}>{contribution.comment}</div>,
      actions: <div className="d-flex"></div>,
    },
  };
};

const createContributionsContractCorrectionTableRow = (
  contribution: BillingsTaxiDriverContributionContractCorrection,
  billingFormData: BillingFormData,
  position: number,
): BillingsTaxiDriverContributionsContractCorrectionTableRow => {
  const contributionType =
    billingsTaxiDriverContributionsContractCorrectionHelper.getContributionTypeTranslation(
      contribution.type,
    );

  switch (contribution.type) {
    case BillingsTaxiDriverContributionContractCorrectionType.BASE_AMOUNT_EXTERNAL_TAXI:
      return {
        id: `${contribution.type}-${position}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          distance: (
            <div title={String(contribution.distance)}>
              {String(contribution.distance)}
            </div>
          ),
          rate: (
            <div
              title={(
                (contribution.amount ?? 0) / (contribution.distance ?? 0)
              ).toFixed(2)}
            >
              {Number(
                (
                  (contribution.amount ?? 0) / (contribution.distance ?? 0)
                ).toFixed(2),
              )}
            </div>
          ),
          amount: (
            <div title={String(contribution.amount)}>
              {String(contribution.amount)}
            </div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    case BillingsTaxiDriverContributionContractCorrectionType.CONTRACT_CORRECTION_RATE:
      return {
        id: `${contribution.type}-${position}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          distance: (
            <div title={String(billingFormData.distance)}>
              {billingFormData.distance}
            </div>
          ),
          rate: (
            <div title={String(contribution.rate)}>
              {String(contribution.rate)}
            </div>
          ),
          amount: (
            <div
              title={String(
                (billingFormData.distance ?? 0) * (contribution.rate ?? 0),
              )}
            >
              {(billingFormData.distance ?? 0) * (contribution.rate ?? 0)}
            </div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    default:
      return {
        id: String(position),
        value: {
          type: <div></div>,
          amount: <div></div>,
          distance: <div></div>,
          rate: <div></div>,
          actions: <div className="d-flex"></div>,
        },
      };
  }
};

const createTabsData = (
  contributions: BillingFormDataContributions,
  isDataLoading: boolean,
  billingFormData: BillingFormData,
): TabsData => {
  const translations =
    billingsTranslationsHelper.getTaxiDriverContributionsTranslations();

  const bonusTableRows: BillingsTaxiDriverContributionsBonusTableRow[] =
    contributions.bonus.map((contribution, index) =>
      createContributionsBonusTableRow(contribution, index),
    );

  const penaltyTableRows: BillingsTaxiDriverContributionsPenaltyTableRow[] =
    contributions.penalty.map((contribution, index) =>
      createContributionsPenaltyTableRow(contribution, index),
    );

  const contractCorrectionTableRows: BillingsTaxiDriverContributionsContractCorrectionTableRow[] =
    contributions.contractCorrection.map((contribution, index) =>
      createContributionsContractCorrectionTableRow(
        contribution,
        billingFormData,
        index,
      ),
    );

  const BonusTableContent = (
    <BillingsTaxiDriverContributionsBonusTableComponent
      isLoading={isDataLoading}
      rows={bonusTableRows}
    />
  );

  const PenaltyTableContent = (
    <BillingsTaxiDriverContributionsPenaltyTableComponent
      isLoading={isDataLoading}
      rows={penaltyTableRows}
    />
  );

  const ContractCorrectionTableContent = (
    <BillingsTaxiDriverContributionsContractCorrectionTableComponent
      isLoading={isDataLoading}
      rows={contractCorrectionTableRows}
    />
  );

  const tabsData: TabsData = [
    {
      label: translations.bonus.title,
      content: BonusTableContent,
      counter: contributions.bonus.filter((contribution) => contribution.type)
        .length,
      totalTableRows: bonusTableRows.length,
    },
    {
      label: translations.penalty.title,
      content: PenaltyTableContent,
      counter: contributions.penalty.filter((contribution) => contribution.type)
        .length,
      totalTableRows: penaltyTableRows.length,
    },
    {
      label: translations.contractCorrection.title,
      content: ContractCorrectionTableContent,
      counter: contributions.contractCorrection.filter(
        (contribution) => contribution.type,
      ).length,
      totalTableRows: contractCorrectionTableRows.length,
    },
  ];

  return tabsData;
};

const billingDataFactory = {
  createBillingData,
  createTabsData,
};

export default billingDataFactory;
